import React, { useState, useEffect } from 'react';
import { Trophy, Star, Clock, AlertTriangle, RefreshCw, User, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';

const RankingEntregadores = () => {
  const { usuario } = useAuth();
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAvaliarAdmin, setOpenAvaliarAdmin] = useState(false);
  const [entregadorSelecionado, setEntregadorSelecionado] = useState(null);
  const [notaAdmin, setNotaAdmin] = useState(3);
  const [observacoes, setObservacoes] = useState('');
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    carregarRanking();
  }, []);

  const carregarRanking = async () => {
    setLoading(true);
    try {
      const response = await api.get('/ranking');
      setRanking(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar ranking');
    } finally {
      setLoading(false);
    }
  };

  const abrirModalAvaliar = (entregador) => {
    setEntregadorSelecionado(entregador);
    setNotaAdmin(3);
    setObservacoes('');
    setMotivo('');
    setOpenAvaliarAdmin(true);
  };

  const handleAvaliarAdmin = async () => {
    if (!entregadorSelecionado) return;
    try {
      await api.post(`/avaliacoes/admin?admin_id=${usuario.id}`, {
        entregador_id: entregadorSelecionado.entregador_id,
        nota: notaAdmin,
        observacoes: observacoes.trim() || null,
        motivo: motivo.trim() || null,
      });
      toast.success('Avaliação registrada com sucesso!');
      setOpenAvaliarAdmin(false);
      carregarRanking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao avaliar entregador');
    }
  };

  const getMedalhaPosicao = (pos) => {
    if (pos === 1) return { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: '🥇' };
    if (pos === 2) return { color: 'bg-gray-100 text-gray-700 border-gray-300', icon: '🥈' };
    if (pos === 3) return { color: 'bg-orange-100 text-orange-700 border-orange-300', icon: '🥉' };
    return { color: 'bg-slate-50 text-slate-600 border-slate-200', icon: `#${pos}` };
  };

  return (
    <DashboardLayout title="Ranking de Entregadores">
      <div data-testid="ranking-entregadores-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Ranking de Entregadores</h1>
              <p className="text-sm text-slate-500">Classificação por desempenho e avaliações</p>
            </div>
          </div>
          <Button onClick={carregarRanking} disabled={loading} variant="outline" data-testid="btn-atualizar-ranking">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {ranking.length === 0 ? (
          <div className="bg-white rounded-2xl border p-12 text-center">
            <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nenhum entregador no ranking ainda</p>
            <p className="text-sm text-slate-400 mt-1">O ranking aparecerá após as primeiras entregas e avaliações</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Pos.</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Entregador</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">Score</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">Estrelas</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">Entregas</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">Tempo Médio</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">Reclamações</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">Nota Admin</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ranking.map((r) => {
                  const medalha = getMedalhaPosicao(r.posicao_ranking);
                  return (
                    <tr key={r.entregador_id} className="hover:bg-slate-50" data-testid={`ranking-row-${r.entregador_id}`}>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center justify-center min-w-[40px] px-2 py-1 rounded-full border text-sm font-bold ${medalha.color}`}>
                          {medalha.icon}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-orange-600" />
                          </div>
                          <span className="text-sm font-medium text-slate-900">{r.empresa_nome || 'Sem nome'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-bold text-orange-600">{r.score_final?.toFixed(1) || '0.0'}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span className="font-medium">{r.media_estrelas?.toFixed(2) || '—'}</span>
                          <span className="text-xs text-slate-500">({r.total_avaliacoes})</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-sm">{r.total_entregas}</td>
                      <td className="px-4 py-3 text-center text-sm">
                        <Clock className="w-3 h-3 inline mr-1 text-slate-400" />
                        {r.tempo_medio_minutos > 0 ? `${r.tempo_medio_minutos.toFixed(0)} min` : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-sm font-medium ${r.indice_reclamacao > 10 ? 'text-red-600' : 'text-green-600'}`}>
                          {r.indice_reclamacao?.toFixed(1) || '0.0'}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-sm">{r.nota_admin?.toFixed(2) || '—'}</td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => abrirModalAvaliar(r)}
                          data-testid={`btn-avaliar-admin-${r.entregador_id}`}
                        >
                          <Award className="w-4 h-4 mr-1" />
                          Avaliar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal de Avaliação Admin */}
        <Dialog open={openAvaliarAdmin} onOpenChange={setOpenAvaliarAdmin}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Avaliar Entregador</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Entregador</p>
                <p className="font-medium">{entregadorSelecionado?.empresa_nome}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Nota (1 a 5)
                </label>
                <div className="flex gap-2 justify-center">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => setNotaAdmin(n)}
                      className="p-1"
                      data-testid={`btn-nota-admin-${n}`}
                    >
                      <Star
                        className={`w-9 h-9 transition-colors ${
                          n <= notaAdmin ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Motivo (opcional)
                </label>
                <Input
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex: Pontualidade, Profissionalismo"
                  data-testid="input-motivo-avaliacao-admin"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Observações (opcional)
                </label>
                <Textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  rows={3}
                  placeholder="Detalhes da avaliação..."
                  data-testid="textarea-obs-avaliacao-admin"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setOpenAvaliarAdmin(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button
                  onClick={handleAvaliarAdmin}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                  data-testid="btn-confirmar-avaliacao-admin"
                >
                  <Award className="w-4 h-4 mr-2" />
                  Confirmar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default RankingEntregadores;
